/**
 * Platform / device helpers for the download flow. Kept in their own module with
 * NO API or store imports so they can be unit-tested (platform.test.ts) without
 * dragging in the HttpClient/Redux/lottie tree that `utils.ts` pulls through
 * `pingDownloadLog`. Re-exported from `utils.ts` so existing callers keep their
 * `components/DownloadButton/utils` import path.
 */

/**
 * True on iOS / iPadOS. Every browser on iOS is WebKit under the hood, so this
 * gates the shared iOS download fallback (REF-5796) — it is a device check, not
 * a Safari-brand check. iPadOS 13+ Safari sends a desktop-Mac user agent, so a
 * Mac UA that also reports multiple touch points is treated as an iPad (real
 * Macs report 0 touch points). Args default to the live navigator values but are
 * injectable for tests.
 */
export const isIosDevice = (
  ua: string = typeof navigator === "undefined" ? "" : navigator.userAgent || "",
  maxTouchPoints: number = typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints || 0
): boolean => {
  if (/iP(hone|ad|od)/.test(ua)) {
    return true;
  }

  return /Mac/.test(ua) && maxTouchPoints > 1;
};

/**
 * Open `url` in a new browser tab — the iOS/iPadOS download fallback. iOS Safari
 * ignores a programmatic `<a download>` click that lands after an `await` (the
 * user-activation window has already closed), so the download silently never
 * starts and no tab or prompt appears (REF-5796). Opening the image in a new tab
 * lets the user long-press → "Save to Photos" / share instead.
 *
 * MUST be called synchronously inside the user's click handler (before any
 * `await`) for the activation to still be live; on activation-less paths
 * (WebSocket completion) it degrades to best-effort and may be popup-blocked.
 *
 * Implemented as a synthetic anchor click rather than `window.open`: WebKit's
 * popup blocker treats a user-initiated anchor navigation more leniently, and
 * `rel="noopener noreferrer"` severs the opener declaratively. (Passing
 * `noopener` to `window.open` is a trap — the spec makes it return `null` even
 * on success, so a success-check + fallback would open a second tab.)
 */
export const openInNewTab = (url: string): void => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
